import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Building2, Users, UserPlus, ShieldCheck, Cpu, RefreshCw, AlertTriangle, CheckCircle2, Palette, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Desktop Probe lockdown: which apps the IT-admin allows the probe to drive.
const PROBE_APPS = ['Word', 'Excel', 'Outlook', 'PowerPoint', 'Browser', 'Notepad'];

const card = { background: '#0f1830', border: '1px solid #1e2b4a', borderRadius: 12, padding: 20, marginBottom: 18 };
const label = { fontSize: 12, color: '#8aa0c6', textTransform: 'uppercase', letterSpacing: 0.5 };
const input = { width: '100%', padding: '10px 12px', background: '#0b1222', border: '1px solid #24345c', borderRadius: 8, color: '#e6edf8' };
const btn = { padding: '10px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 };

const OrgAdmin = () => {
  const [org, setOrg] = useState(null);
  const [role, setRole] = useState(null);
  const [members, setMembers] = useState([]);
  const [policy, setPolicy] = useState({});
  const [form, setForm] = useState({ email: '', full_name: '', password: '', org_role: 'member' });
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branding, setBranding] = useState({});
  const [sso, setSso] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const me = await axios.get(`${API}/compass/org/me`, authHeaders());
      setOrg(me.data.organization);
      setRole(me.data.org_role);
      setPolicy(me.data.organization?.probe_policy || {});
      setBranding(me.data.organization?.branding || {});
      if (me.data.org_role === 'org_admin') {
        const m = await axios.get(`${API}/compass/org/members`, authHeaders());
        setMembers(m.data.members || []);
        try { const sc = await axios.get(`${API}/compass/org/sso-config`, authHeaders()); setSso(sc.data.sso_config || {}); } catch (e) {}
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'Kon organisatie niet laden');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addMember = async (e) => {
    e.preventDefault(); setErr(null); setMsg(null);
    try {
      await axios.post(`${API}/compass/org/members`, form, authHeaders());
      setMsg(`Account aangemaakt voor ${form.email}`);
      setForm({ email: '', full_name: '', password: '', org_role: 'member' });
      load();
    } catch (e2) { setErr(e2.response?.data?.detail || 'Toevoegen mislukt'); }
  };

  const toggleApp = async (app) => {
    const allowed = new Set(policy.allowed_apps || PROBE_APPS);
    allowed.has(app) ? allowed.delete(app) : allowed.add(app);
    const next = { ...policy, allowed_apps: Array.from(allowed) };
    setPolicy(next);
    try {
      await axios.patch(`${API}/compass/org/probe-policy`, next, authHeaders());
      setMsg('Probe-policy opgeslagen');
    } catch (e) { setErr(e.response?.data?.detail || 'Policy opslaan mislukt'); }
  };

  const saveBranding = async () => {
    setErr(null); setMsg(null);
    try {
      const res = await axios.patch(`${API}/compass/org/branding`, branding, authHeaders());
      setBranding(res.data.branding || branding); setMsg('Huisstijl opgeslagen');
    } catch (e) { setErr(e.response?.data?.detail || 'Huisstijl opslaan mislukt'); }
  };

  const saveSso = async () => {
    setErr(null); setMsg(null);
    try {
      const res = await axios.patch(`${API}/compass/org/sso-config`, sso, authHeaders());
      setSso(res.data.sso_config || sso); setMsg('SSO-configuratie opgeslagen');
    } catch (e) { setErr(e.response?.data?.detail || 'SSO opslaan mislukt'); }
  };

  if (loading) return <div style={{ padding: 40, color: '#8aa0c6' }}><RefreshCw className="spin" size={18} /> Laden…</div>;

  if (!org) return (
    <div style={{ ...card, maxWidth: 560, margin: '40px auto', textAlign: 'center' }}>
      <Building2 size={32} color="#3b82f6" />
      <h2 style={{ color: '#e6edf8' }}>Geen organisatie</h2>
      <p style={{ color: '#8aa0c6' }}>Dit account hoort niet bij een bedrijf. Bedrijven kunnen zich aanmelden via de bedrijfs-registratie.</p>
    </div>
  );

  if (role !== 'org_admin') return (
    <div style={{ ...card, maxWidth: 560, margin: '40px auto', textAlign: 'center' }}>
      <ShieldCheck size={32} color="#3b82f6" />
      <h2 style={{ color: '#e6edf8' }}>{org.name}</h2>
      <p style={{ color: '#8aa0c6' }}>Je bent lid van deze organisatie. Beheer is voorbehouden aan de IT-beheerder.</p>
    </div>
  );

  const seatsUsed = org.seats_used ?? members.length;
  const allowed = new Set(policy.allowed_apps || PROBE_APPS);

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: 24 }}>
      <h1 style={{ color: '#e6edf8', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Building2 size={24} color="#3b82f6" /> {org.name} — IT-beheer
      </h1>
      {msg && <div style={{ ...card, borderColor: '#1f7a4d', color: '#7ee2a8', display: 'flex', gap: 8 }}><CheckCircle2 size={16} /> {msg}</div>}
      {err && <div style={{ ...card, borderColor: '#7a2a2a', color: '#f2a3a3', display: 'flex', gap: 8 }}><AlertTriangle size={16} /> {err}</div>}

      {/* Org overview */}
      <div style={card}>
        <div style={label}>Organisatie</div>
        <div style={{ display: 'flex', gap: 32, marginTop: 10, color: '#e6edf8' }}>
          <div><div style={label}>Plan</div>{org.plan}</div>
          <div><div style={label}>Seats</div>{seatsUsed} / {org.seats}</div>
          <div><div style={label}>Subdomein</div>{org.slug}.{window.location.hostname}</div>
          <div><div style={label}>Status</div>{org.status}</div>
        </div>
      </div>

      {/* Members */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ ...label, display: 'flex', alignItems: 'center', gap: 6 }}><Users size={14} /> Medewerkers ({members.length})</div>
          <button style={{ ...btn, background: 'transparent', color: '#8aa0c6' }} onClick={load}><RefreshCw size={14} /></button>
        </div>
        <table style={{ width: '100%', marginTop: 12, borderCollapse: 'collapse', color: '#e6edf8' }}>
          <thead><tr style={{ color: '#8aa0c6', textAlign: 'left', fontSize: 12 }}>
            <th style={{ padding: 8 }}>Naam</th><th>E-mail</th><th>Rol</th><th>Actief</th>
          </tr></thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} style={{ borderTop: '1px solid #1e2b4a' }}>
                <td style={{ padding: 8 }}>{m.full_name}</td>
                <td>{m.email}</td>
                <td>{m.org_role === 'org_admin' ? 'IT-beheerder' : 'Medewerker'}</td>
                <td>{m.is_active ? '✓' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add member */}
      <div style={card}>
        <div style={{ ...label, display: 'flex', alignItems: 'center', gap: 6 }}><UserPlus size={14} /> Medewerker toevoegen</div>
        <form onSubmit={addMember} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
          <input style={input} placeholder="Volledige naam" value={form.full_name} required onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          <input style={input} type="email" placeholder="E-mail" value={form.email} required onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input style={input} type="password" placeholder="Tijdelijk wachtwoord" value={form.password} required minLength={6} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <select style={input} value={form.org_role} onChange={(e) => setForm({ ...form, org_role: e.target.value })}>
            <option value="member">Medewerker</option>
            <option value="org_admin">IT-beheerder</option>
          </select>
          <button type="submit" style={btn}>Account aanmaken</button>
        </form>
        <p style={{ ...label, marginTop: 8, textTransform: 'none' }}>Privacy: jij beheert accounts + policy — je ziet géén tool-data of Emma van medewerkers.</p>
      </div>

      {/* Probe lockdown */}
      <div style={card}>
        <div style={{ ...label, display: 'flex', alignItems: 'center', gap: 6 }}><Cpu size={14} /> Desktop Probe — toegestane apps</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
          {PROBE_APPS.map((app) => (
            <button key={app} onClick={() => toggleApp(app)}
              style={{ ...btn, background: allowed.has(app) ? '#3b82f6' : 'transparent', color: allowed.has(app) ? '#fff' : '#8aa0c6', border: '1px solid #24345c' }}>
              {app}
            </button>
          ))}
        </div>
        <p style={{ ...label, marginTop: 8, textTransform: 'none' }}>De probe van elke medewerker handhaaft deze policy (alleen toegestane apps; mailen/verwijderen altijd via goedkeuring).</p>
      </div>

      {/* Branding */}
      <div style={card}>
        <div style={{ ...label, display: 'flex', alignItems: 'center', gap: 6 }}><Palette size={14} /> Huisstijl (login-pagina)</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
          <input style={input} placeholder="Logo-URL (https://…)" value={branding.logo_url || ''} onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })} />
          <input style={input} placeholder="Primaire kleur (#3b82f6)" value={branding.primary_color || ''} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} />
          <input style={input} placeholder="Login-titel" value={branding.login_title || ''} onChange={(e) => setBranding({ ...branding, login_title: e.target.value })} />
          <input style={input} placeholder="Login-ondertitel" value={branding.login_subtitle || ''} onChange={(e) => setBranding({ ...branding, login_subtitle: e.target.value })} />
        </div>
        <button style={{ ...btn, marginTop: 12 }} onClick={saveBranding}>Huisstijl opslaan</button>
        <p style={{ ...label, marginTop: 8, textTransform: 'none' }}>Zichtbaar op {org.slug}.{window.location.hostname} — medewerkers loggen in onder je eigen merk.</p>
      </div>

      {/* SSO */}
      <div style={card}>
        <div style={{ ...label, display: 'flex', alignItems: 'center', gap: 6 }}><KeyRound size={14} /> Single Sign-On (OIDC)</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
          <input style={input} placeholder="Issuer URL (bv. https://login.microsoftonline.com/<tenant>/v2.0)" value={sso.issuer || ''} onChange={(e) => setSso({ ...sso, issuer: e.target.value })} />
          <input style={input} placeholder="Client ID" value={sso.client_id || ''} onChange={(e) => setSso({ ...sso, client_id: e.target.value })} />
          <input style={input} type="password" placeholder={sso.client_secret_set ? '•••••• (ingesteld — leeg laten = behouden)' : 'Client secret'} value={sso.client_secret || ''} onChange={(e) => setSso({ ...sso, client_secret: e.target.value })} />
          <input style={input} placeholder="Scopes (openid email profile)" value={sso.scopes || ''} onChange={(e) => setSso({ ...sso, scopes: e.target.value })} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 12, color: '#e6edf8', alignItems: 'center' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={!!sso.enabled} onChange={(e) => setSso({ ...sso, enabled: e.target.checked })} /> SSO inschakelen</label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={!!sso.auto_provision} onChange={(e) => setSso({ ...sso, auto_provision: e.target.checked })} /> Auto-provisioning (nieuwe medewerker bij 1e login, seat-gecheckt)</label>
        </div>
        <button style={{ ...btn, marginTop: 12 }} onClick={saveSso}>SSO opslaan</button>
        <p style={{ ...label, marginTop: 8, textTransform: 'none' }}>Redirect-URI bij je IdP: <code style={{ color: '#7ee2a8' }}>{window.location.origin}/api/compass/org/sso/{org.slug}/callback</code></p>
      </div>
    </div>
  );
};

export default OrgAdmin;
